import { useCallback, useMemo } from "react";
import { v4 as uuid } from "uuid";

import { Series, useCreateSeriesMutation, useGetAllSeriesQuery } from "../../../redux/slices";
import { SportPlay } from "../../../types";
import { TSelectItem, useSelect } from "../../select";
import { UseSelect } from "../types";

export type SelectSeriesItem = TSelectItem<Series>;

export const useSelectSeries = <TPlay extends SportPlay>({ editingPlay, updatePlay }: UseSelect<TPlay>) => {
  const { data: series } = useGetAllSeriesQuery();

  const seriesItems: SelectSeriesItem[] = useMemo(
    (): SelectSeriesItem[] => series?.map((s) => ({ ...s, label: s.name })) || [],
    [series]
  );

  const selectedSeries: SelectSeriesItem | undefined = useMemo(
    () => (editingPlay.series ? { ...editingPlay.series, label: editingPlay.series.name } : undefined),
    [editingPlay]
  );

  const handleChangeSeries = (item: SelectSeriesItem) => {
    updatePlay((prevValue) => ({ ...prevValue, series: item }));
  };

  const [createSeries] = useCreateSeriesMutation();
  const handleNewSeries = (query: string): TSelectItem<Series> => {
    const id = uuid();
    return {
      id,
      label: query,
      name: query,
      account_id: 0,
      createdTimestamp: "",
      updatedTimestamp: "",
      deleted: false,
      displayOrder: 0,
      noOfPlays: 0,
      series_id: id,
    };
  };

  const createNewSeries = useCallback(
    async (item: TSelectItem<Series>) => {
      const oldSeries = editingPlay.series;

      try {
        updatePlay((prevValue) => ({ ...prevValue, series: item }));

        const series = await createSeries({ name: item.name }).unwrap();

        updatePlay((prevValue) => ({ ...prevValue, series: series.data }));
      } catch (err) {
        console.error(err);
        updatePlay((prevValue) => ({ ...prevValue, series: oldSeries }));
      }
    },
    [createSeries, updatePlay, editingPlay]
  );

  const seriesSelect = useSelect({
    items: seriesItems,
    initialSelectedItem: selectedSeries,
    onChange: handleChangeSeries,
    createItem: createNewSeries,
  });

  return { seriesSelect, handleNewSeries };
};
